import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoriesList } from '@/mocks/articles';
import { useJsonLd } from '@/hooks/useJsonLd';

const allCategoryTabs = [
  { slug: 'influencer-scandal', label: '网红塌房' },
  { slug: 'celebrity-scandal', label: '明星黑料' },
  { slug: 'blogger-contrast', label: '博主反差' },
  { slug: 'exposure-gate', label: '网曝门事件' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categoriesList.find((c) => c.slug === slug);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "http://ahyuntong.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": category?.name || slug
      }
    ]
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category?.name || slug} - 51吃瓜黑料`,
    "description": category?.description || "吃瓜爆料分类",
    "url": `http://ahyuntong.cn/category/${slug}`,
    "inLanguage": "zh-CN",
    "isPartOf": {
      "@type": "WebSite",
      "name": "51吃瓜黑料",
      "url": "http://ahyuntong.cn/"
    }
  };

  useJsonLd(breadcrumbSchema);
  useJsonLd(collectionSchema);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <p className="text-foreground-400 text-lg mb-4">分类未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
          <div className="absolute inset-0 bg-foreground-950"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          <div className="relative z-10 w-full h-full flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <nav className="flex items-center gap-2 text-sm mb-6" aria-label="面包屑导航">
                  <Link to="/" className="text-background-50/60 hover:text-background-50 transition-colors">首页</Link>
                  <span className="text-background-50/40">/</span>
                  <span className="text-background-50">{category.name}</span>
                </nav>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">
                  {category.name}专区
                </h1>
                <p className="text-background-50/70 text-sm md:text-base max-w-xl">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tab Switcher */}
        <section className="py-6 bg-background-50 border-b border-background-200">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-wrap items-center gap-2">
                {allCategoryTabs.map((tab) => (
                  <Link
                    key={tab.slug}
                    to={`/category/${tab.slug}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      slug === tab.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                    }`}
                  >
                    {tab.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
                {category.articles.map((article) => (
                  <Link
                    key={article.id}
                    to="/category/all"
                    className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:ring-2 hover:ring-primary-200"
                  >
                    <div className="relative w-full h-52 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-xs md:text-sm mb-4 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">{article.views} 阅读</span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Sub Categories */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10">
                <p className="text-foreground-500 font-label text-xs tracking-wider uppercase mb-2">子分类</p>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  更精准的瓜
                  <br />
                  <span className="text-foreground-400">按话题深度筛选</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {category.subCategories.map((sub) => (
                  <Link
                    key={sub.slug}
                    to={`/category/${slug}`}
                    className="group block bg-background-100 rounded-lg p-5 transition-all duration-300 hover:bg-accent-50"
                  >
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-1 group-hover:text-accent-700 transition-colors duration-200">
                      {sub.name}
                    </h3>
                    <p className="text-foreground-500 text-xs">浏览全部内容</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}